import React from 'react';
import { Typography } from '@mui/material';
import { isUnrecognisedCarrierStatus } from 'src/components/Constants';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Family-level notice for genotypes carrying a carrier status TrakGene cannot
// interpret — typically an integration sending a vocabulary we do not share.
//
// Such a value renders verbatim in the pedigree label but derives no +/- marker, and
// that gap is invisible precisely because the label looks correct. The per-genotype
// warning lives in the sidebar, which nobody opens on the one node that needs it, so
// this surfaces the count on the chart itself AND explains why those labels are
// italicised. Renders nothing when every value is understood, so the host can place it
// unconditionally (cf. VariantPathogenicityLegend, which this follows).

/**
 * Every genotype in the family whose carrier status cannot be interpreted.
 * @param {Array<{ pid?: number, genotypes?: Array<object> }>} members
 * @returns {Array<{ pid: number|undefined, gene: string, value: string }>}
 */
export const collectUnrecognisedCarrierStatuses = (members) => {
  const out = [];
  members?.forEach((m) => {
    m?.genotypes?.forEach((g) => {
      if (!isUnrecognisedCarrierStatus(g?.carrierStatus)) return;
      out.push({
        pid: m?.pid,
        gene: g?.name || g?.gene || '',
        value: String(g.carrierStatus).trim()
      });
    });
  });
  return out;
};

export default function UnrecognisedCarrierStatusNotice({ members }) {
  const t = useAutoTranslation();

  const found = React.useMemo(
    () => collectUnrecognisedCarrierStatuses(members),
    [members]
  );

  if (!found.length) return null;

  // The distinct values matter more than the occurrence count: one wrong vocabulary
  // repeated across a family is a single integration problem, and naming the value is
  // what lets someone act on it.
  const values = [...new Set(found.map((f) => f.value))];

  return (
    <div
      style={{
        marginTop: 8,
        borderTop: '1px solid #e2e8f0',
        paddingTop: 8,
        pointerEvents: 'auto'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span
          style={{
            color: '#F5A623',
            fontSize: '0.95rem',
            lineHeight: 1.3,
            flexShrink: 0
          }}
        >
          ⚠
        </span>
        <div>
          <Typography style={{ fontSize: '0.8rem', fontWeight: 600 }}>
            {found.length === 1
              ? t('1 genotype has an unrecognised carrier status')
              : `${found.length} ${t(
                  'genotypes have an unrecognised carrier status'
                )}`}
          </Typography>
          <Typography style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>
            {values.map((v) => `"${v}"`).join(', ')} —{' '}
            {t(
              'shown in italics on the pedigree. No carrier symbol has been derived. Check the source record.'
            )}
          </Typography>
        </div>
      </div>
    </div>
  );
}
